import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const hasInitialized = useRef(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;
    // Check if user is already logged in (from localStorage)
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("accessToken");
    // console.log("Saved user from localStorage:", savedUser);
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
    if (savedToken) {
      setAccessToken(savedToken);
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    if (userData.accessToken) {
      localStorage.setItem("accessToken", userData.accessToken);
      setAccessToken(userData.accessToken);
    }
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const updateUser = useCallback((updatedFields) => {
    setUser((prevUser) => {
      if (!updatedFields || typeof updatedFields !== "object") {
        return prevUser;
      }
      // Avoid re-renders if the values are identical
      const hasChanges = Object.keys(updatedFields).some(
        (key) => prevUser?.[key] !== updatedFields[key]
      );
      if (!hasChanges) {
        return prevUser;
      }
      const newUser = { ...(prevUser || {}), ...updatedFields };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("isGoForSubscription");
    localStorage.removeItem("subModalSkipCount");
    queryClient.clear();
    setUser(null);
    setAccessToken(null);
    setIsAuthenticated(false);
  }, [queryClient]);

  const value = {
    isAuthenticated,
    user,
    accessToken,
    updateUser,
    logout,
    login,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
